<script setup>
import { ref, onMounted } from 'vue'
import { useUserStore } from '@/stores/user'
import { useRouter, useRoute} from 'vue-router'

import { useQueryStore } from '@/stores/query'
let queryStore = useQueryStore()

import ContactModal from '@/components/modals/Contact.vue'
import SidebarExpandable from '@/components/SidebarExpandable.vue'

import { useMainStore } from '@/stores/main'
const mainStore = useMainStore()

const userStore = useUserStore()
const router = useRouter()
const route = useRoute()

import { computed } from 'vue'
const hasEventAccess = computed(() => {
  return userStore.accountType >= 4 || userStore.accountType === 1337
})

// Admin tools configuration
const adminTools = [
  { icon: ['fas', 'lightbulb'], label: 'guides', route: '/guides' },
  { icon: ['fas', 'comments'], label: 'channels', route: '/channels' },
  { icon: ['fas', 'address-card'], label: 'providers', route: '/admin/providers' },
  { icon: ['fas', 'users'], label: 'accounts', route: '/admin/accounts' },
  { icon: ['fas', 'building'], label: 'organizations', route: '/admin/organizations' },
  { icon: ['fas', 'bullhorn'], label: 'notifications', route: '/admin/notifications' },
  { icon: ['fas', 'ticket'], label: 'tickets', route: '/admin/tickets' },
  { icon: ['fas', 'newspaper'], label: 'articles', route: '/admin/articles' }
]

// Sidebar expansion state
const isExpanded = ref(false)
const emit = defineEmits(['update:expanded'])

onMounted(() => {
  // Load saved state from localStorage
  const saved = localStorage.getItem('webscout-sidebar-expanded')
  if (saved !== null) {
    isExpanded.value = saved === 'true'
  }
  // Emit initial state
  emit('update:expanded', isExpanded.value)
})

function toggleExpanded() {
  isExpanded.value = !isExpanded.value
  localStorage.setItem('webscout-sidebar-expanded', isExpanded.value.toString())
  // Emit the new state
  emit('update:expanded', isExpanded.value)
}

async function logout() {
  await userStore.logout()
}

function clear_query() {
  queryStore.result = null
  queryStore.error = ""
  return true
}

//mainStore.show_modal(qf, { fileName: file.name, domains: res.domains, ips: res.ips})
function contact() {
  mainStore.show_modal(ContactModal, { sender: userStore.email, name: userStore.name })
}
</script>

<template>
    <div class="sidebar-wrapper">
    <nav id="sidebar" :class="{ 'expanded': isExpanded }">
      <ul class="navbar-nav mb-auto navbar-top" ondragstart="return false;">
        <li class="nav-item-wrapper logo-wrapper">
          <router-link class="sidebar-nav logo-nav" to="/dashboard">
            <img src="~@/assets/ws_logo_idea.svg" class="logo" height="30"/>
            <img src="~@/assets/webscout_white.svg" class="logo-text" height="20"/>
          </router-link>
        </li>

        <li class="nav-item-wrapper">
          <router-link class="sidebar-nav" to="/dashboard">
            <font-awesome-icon class="icon" :icon="['fas', 'table-cells-large']" />
            <span class="nav-text">dashboard</span>
            <font-awesome-icon v-if="!hasEventAccess" class="nav-lock-icon" :icon="['fas', 'lock']" />
            <div class="nav-popout" v-if="!isExpanded">
              <span>dashboard</span>
            </div>
          </router-link>
        </li>
        <li class="nav-item-wrapper">
          <router-link class="sidebar-nav" to="/enrich" @click.prevent="clear_query" data-guide="sidebar-enrich">
            <font-awesome-icon class="icon" :icon="['fas', 'globe']" />
            <span class="nav-text">enrich</span>
            <div class="nav-popout" v-if="!isExpanded">
              <span>enrich</span>
            </div>
          </router-link>
        </li>
        <li class="nav-item-wrapper">
          <router-link class="sidebar-nav" to="/search/event" @click.prevent="clear_query">
            <font-awesome-icon class="icon" :icon="['fas', 'search']" />
            <span class="nav-text">search</span>
            <font-awesome-icon v-if="!hasEventAccess" class="nav-lock-icon" :icon="['fas', 'lock']" />
            <div class="nav-popout" v-if="!isExpanded">
              <span>search</span>
            </div>
          </router-link>
        </li>
        <li class="nav-item-wrapper">
          <router-link class="sidebar-nav"  to="/signals" @click.prevent="clear_query">
            <font-awesome-icon  class="icon" :icon="['fas', 'satellite-dish']" />
            <span class="nav-text">signals</span>
            <div class="nav-popout" v-if="!isExpanded">
              <span>signals</span>
            </div>
          </router-link>
        </li>
        <li class="nav-item-wrapper">
          <router-link class="sidebar-nav" to="/saved-searches">
            <font-awesome-icon class="icon" :icon="['fas', 'bookmark']" />
            <span class="nav-text">saved searches</span>
            <font-awesome-icon v-if="!hasEventAccess" class="nav-lock-icon" :icon="['fas', 'lock']" />
            <div class="nav-popout" v-if="!isExpanded">
              <span>saved searches</span>
            </div>
          </router-link>
        </li>
        <!--
        <li class="nav-item-wrapper">
          <router-link class="sidebar-nav"  to="/tools" @click.prevent="clear_query">
            <font-awesome-icon  class="icon" :icon="['fas', 'toolbox']" />
            <span class="nav-text">enrichment tools</span>
            <div class="nav-popout" v-if="!isExpanded">
              <span>enrichment tools</span>
            </div>
          </router-link>
        </li>
        -->
      </ul>
      <ul class="navbar-nav navbar-bottom">
        <li class="nav-item-wrapper">
          <router-link class="sidebar-nav nav-link" to="/sources">
            <font-awesome-icon class="icon" :icon="['fas', 'diagram-project']" />
            <span class="nav-text">sources</span>
            <div class="nav-popout" v-if="!isExpanded"><span>sources</span></div>
          </router-link>
        </li>
        <li class="nav-item-wrapper">
          <router-link class="sidebar-nav nav-link" to="/docs/api">
            <font-awesome-icon  class="icon" :icon="['fas', 'code']" />
            <span class="nav-text">api documentation</span>
            <div class="nav-popout" v-if="!isExpanded">
              <span>api documentation</span>
            </div>
          </router-link>
        </li>
        <li class="nav-item-wrapper">
          <router-link class="sidebar-nav"  to="/rfi">
            <font-awesome-icon  class="icon" :icon="['fas', 'file-alt']" />
            <span class="nav-text">request for information</span>
            <div class="nav-popout" v-if="!isExpanded">
              <span>request for information</span>
            </div>
          </router-link>
        </li>
        <li v-if="userStore.accountType != 1337" class="nav-item-wrapper">
          <router-link class="sidebar-nav nav-link" to="/tickets">
            <font-awesome-icon class="icon" :icon="['fas', 'ticket']" />
            <span class="nav-text">tickets</span>
            <div class="nav-popout" v-if="!isExpanded">
              <span>tickets</span>
            </div>
          </router-link>
        </li>
        <SidebarExpandable
          v-if="userStore.accountType == 1337"
          parent-label="admin tools"
          :parent-icon="['fas', 'gear']"
          :children="adminTools"
          :is-expanded="isExpanded"
        />
        <li class="nav-item-wrapper">
          <div @click="logout()" class="sidebar-nav logout-nav">
            <font-awesome-icon  class="icon" :icon="['fas', 'lock']" />
            <span class="nav-text">logout</span>
            <div class="nav-popout" v-if="!isExpanded">
              <span>logout</span>
            </div>
          </div>
        </li>
        <li class="nav-item-wrapper">
          <div @click="toggleExpanded()" class="sidebar-nav toggle-nav">
            <font-awesome-icon  class="icon" :icon="['fas', isExpanded ? 'angle-left' : 'angle-right']" />
            <span class="nav-text">{{ isExpanded ? 'collapse' : 'expand' }}</span>
            <div class="nav-popout" v-if="!isExpanded">
              <span>expand</span>
            </div>
          </div>
        </li>
      </ul>
    </nav>
    </div>
</template>
<style scoped>
.sidebar-wrapper {
  position: relative;
  padding: 1rem;
  z-index: 10;
  transition: width 0.3s ease;
  background-color: #191f27;
}

#sidebar {
  padding: 0;
  background: #151a22;
  display: flex;
  flex-direction: column;
  position: relative;
  width: 85px;
  height: calc(100vh - 2rem);
  border-radius: 1rem;
  transition: width 0.3s ease;
}

#sidebar.expanded {
  width: 300px;
}

.nav-item-wrapper {
  position: relative;
  padding: 0.25rem 0.75rem;
}

#sidebar:not(.expanded) .nav-item-wrapper {
  padding: 0.25rem 0.75rem;
}

#sidebar .sidebar-nav {
  padding: 0.75rem 1rem;
  height: auto;
  min-height: 3rem;
  color: #7A8696;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  border-radius: 0.75rem;
  position: relative;
  gap: 0;
}

#sidebar:not(.expanded) .sidebar-nav {
  justify-content: center;
  padding: 0.75rem 0;
  width: 100%;
}

#sidebar .sidebar-nav .icon {
  transition: margin-right 0.3s ease;
  font-size: 1.1rem;
  width: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

#sidebar .logo {
  transition: margin-right 0.3s ease;
}

/* Text visibility in collapsed state */
#sidebar:not(.expanded) .nav-text {
  display: none;
}

#sidebar:not(.expanded) .logo-text {
  display: none;
}

/* Text visibility in expanded state */
#sidebar.expanded .nav-text {
  display: inline;
  white-space: nowrap;
  font-size: 0.95rem;
}

#sidebar.expanded .logo-text {
  display: inline;
  white-space: nowrap;
}

#sidebar.expanded .sidebar-nav .icon {
  margin-right: 0.75rem;
  transition: all 0.2s ease;
}

#sidebar.expanded .sidebar-nav .logo {
  margin-right: 0.75rem;
  transition: all 0.2s ease;
}

/* Popout styles for collapsed state */
.nav-popout {
  display: flex;
  position: absolute;
  left: calc(100% - 1.3rem);
  top: 0;
  height: 100%;
  background: #1d2531;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  align-items: center;
  white-space: nowrap;
  z-index: 100;
  color: white;
  font-size: 0.95rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  text-decoration: none;
}

/* Show popout and background on hover of nav-item-wrapper */
#sidebar:not(.expanded) .nav-item-wrapper:hover .nav-popout {
  opacity: 1;
  pointer-events: auto;
}

#sidebar:not(.expanded) .nav-item-wrapper:hover .sidebar-nav {
  background-color: #1d2531;
  color: white;
}

.nav-popout:hover {
  color: white;
  text-decoration: none;
}

/* Logo - no hover effect */
.logo-wrapper {
  margin-bottom: 0.5rem;
}

#sidebar:not(.expanded) .logo-wrapper {
  padding: 0.25rem 0.75rem;
}

#sidebar:not(.expanded) .logo-nav {
  justify-content: center;
  padding: 0.75rem 0;
}

.logo-nav:hover {
  background-color: transparent !important;
  cursor: default;
}

/* Hover effects - same for both collapsed and expanded */
#sidebar .sidebar-nav:hover {
  color: white;
  cursor: pointer;
  background-color: #1d2531;
}

/* Active state */
.router-link-active {
  color: white !important;
}

.router-link-exact-active {
  color: white !important;
}

.router-link-active.sidebar-nav,
.router-link-exact-active.sidebar-nav {
  background-color: #1d2531;
  color: white !important;
}

.logo-nav.router-link-exact-active {
  background-color: transparent;
}
.logo-nav.router-link-active {
  background-color: transparent;
}

/* Logout styling */
.logout-nav {
  color: #ca5050 !important;
}

.logout-nav:hover {
  color: #ff6b6b !important;
}

/* Lock badge for tier-restricted nav items */
.nav-lock-icon {
  position: absolute;
  top: 8px;
  left: 38px;
  font-size: 0.35rem;
  color: #9CA3AF;
  background: #2a3140;
  border-radius: 50%;
  width: 10px;
  height: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s ease;
}

.sidebar-nav:hover .nav-lock-icon {
  opacity: 0;
}

/* Toggle nav styling */
.toggle-nav {
  color: #7A8696;
}

/* Top and bottom spacing for navbar sections */
.navbar-top {
  padding-top: 0.5rem;
}

.navbar-bottom {
  padding-bottom: 0.5rem;
}
</style>
